import {
  COOKIE_AUTH_USER,
  COOKIE_PROFILE_USER,
  COOKIE_PROPERTIES_RULES,
  COOKIE_UPDATED_COOKIES,
  COOKIE_ACCESS_TOKEN,
  COOKIE_REFRESH_TOKEN,
  COOKIE_EXPIRES_IN
} from '../constants'
import { createCookie, getCookieValue, getExpirationDate } from '../utils'
import type { IAuthUser, IProfileUser, IUpdatedCookies } from '../../interfaces'
import type {
  ICookieAccessToken,
  ICookieExpiresIn
} from '../../interfaces/src/auth.interface'
import { decodeJWTCookieAccessToken } from '../utils/auth0.utils'

const STAGE = process.env.NEXT_PUBLIC_STAGE ?? ''

const IS_DEV_OR_PROD = STAGE === 'dev' || STAGE === 'prod'

export const getCookieAuthUser = () => {
  const authUser = getCookieValue(COOKIE_AUTH_USER)

  if (authUser) {
    const parsedAuthUser = JSON.parse(authUser) as IAuthUser
    return parsedAuthUser
  }

  return null
}

export const getCookieBinaryRol = () => {
  const binaryRole = getCookieValue('binary_role')

  if (binaryRole) {
    return binaryRole
  }

  return null
}

export const getCookieProfileUser = () => {
  const profileUser = getCookieValue(COOKIE_PROFILE_USER)
  if (profileUser) {
    return JSON.parse(profileUser)
  }

  return null
}

export const getCookieAuthorizationToken = () => {
  const authUser = getCookieValue(COOKIE_AUTH_USER)
  if (authUser) {
    const parsedAuthUser = JSON.parse(authUser) as IAuthUser
    const authorizationToken = parsedAuthUser?.token
    return authorizationToken
  }

  return null
}

export const getCookieAuthorizationRoles = () => {
  const authUser = getCookieValue(COOKIE_AUTH_USER)
  if (authUser) {
    const parsedAuthUser = JSON.parse(authUser) as IAuthUser
    const authorizationRoles = parsedAuthUser?.roles
    return authorizationRoles
  }

  return null
}

export const getAuthorizationTokenByAuthUser = (authUser: string) => {
  if (authUser) {
    const parsedAuthUser = JSON.parse(authUser) as IAuthUser
    const authorizationToken = parsedAuthUser?.token
    return authorizationToken
  }

  return null
}

export const getCookiePropertiesRules = () => {
  const propertiesRules = getCookieValue(COOKIE_PROPERTIES_RULES)

  if (propertiesRules) {
    return JSON.parse(propertiesRules)
  }

  return null
}

export const getCookieUpdateCookies = () => {
  const updateCookies = getCookieValue(COOKIE_UPDATED_COOKIES)

  if (updateCookies) {
    return JSON.parse(updateCookies) as IUpdatedCookies
  }

  return null
}

export const createCookieLegacyAuthUser = ({
  authUser
}: {
  authUser: IAuthUser
}) => {
  createCookie({
    name: COOKIE_AUTH_USER,
    value: JSON.stringify(authUser),
    date: getExpirationDate(),
    httpOnly: false,
    secure: IS_DEV_OR_PROD
  })
}
export const createCookieLegacyProfileUser = ({
  profileUser
}: {
  profileUser: IProfileUser
}) => {
  createCookie({
    name: COOKIE_PROFILE_USER,
    value: JSON.stringify(profileUser),
    date: getExpirationDate(),
    httpOnly: false,
    secure: IS_DEV_OR_PROD
  })
}

export const createCookiesAuth0 = ({
  accessToken,
  expiresIn,
  refreshToken
}: {
  accessToken: string
  refreshToken?: string
  expiresIn: number
}) => {
  const valueExpiresIn = {
    value: expiresIn?.toString(),
    lastUpdated: new Date().toUTCString()
  } as ICookieExpiresIn

  createCookie({
    name: COOKIE_ACCESS_TOKEN,
    value: accessToken?.toString(),
    date: getExpirationDate(),
    httpOnly: false,
    secure: IS_DEV_OR_PROD
  })

  if (refreshToken) {
    createCookie({
      name: COOKIE_REFRESH_TOKEN,
      value: refreshToken?.toString(),
      date: getExpirationDate(),
      httpOnly: false,
      secure: IS_DEV_OR_PROD
    })
  }

  createCookie({
    name: COOKIE_EXPIRES_IN,
    value: JSON.stringify(valueExpiresIn),
    date: getExpirationDate(),
    httpOnly: false,
    secure: IS_DEV_OR_PROD
  })
}

export const getCookieRefreshToken = () => {
  const refreshToken = getCookieValue(COOKIE_REFRESH_TOKEN)

  if (refreshToken) {
    return refreshToken
  }

  return null
}

export const getCookieAccessToken = (): {
  accessToken: string
  decodeAccessToken?: ICookieAccessToken
} | null => {
  const accessToken = getCookieValue(COOKIE_ACCESS_TOKEN)

  if (accessToken) {
    const decodeAccessToken = decodeJWTCookieAccessToken(accessToken)
    return { accessToken, decodeAccessToken }
  }

  return null
}

export const getCookieExpiresIn = () => {
  const expiresIn = getCookieValue(COOKIE_EXPIRES_IN)

  if (expiresIn) {
    const parsedExpiresIn = JSON.parse(expiresIn) as ICookieExpiresIn
    return parsedExpiresIn
  }

  return null
}
